/**
 * ScreenContext — lets each screen publish a "what's on screen" snapshot that the Risk Advisor
 * widget attaches to every message, so the advisor can answer about what the user is looking at.
 *
 * A screen publishes via `useScreenContext().setScreenContext({ route, screenName, data })` in a
 * useEffect when its data loads. The widget reads `screen` and only uses `data` when `screen.route`
 * matches the current route (so stale data from a previous screen is never sent).
 */
import React, { createContext, useContext, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";

const ScreenContext = createContext(null);

export function ScreenContextProvider({ children }) {
  const [screen, setScreen] = useState({ route: "", screenName: "", data: null });
  const setScreenContext = useCallback(
    (descriptor) => setScreen((prev) => ({ ...prev, ...descriptor })),
    []
  );
  const value = useMemo(() => ({ screen, setScreenContext }), [screen, setScreenContext]);
  return <ScreenContext.Provider value={value}>{children}</ScreenContext.Provider>;
}

ScreenContextProvider.propTypes = {
  children: PropTypes.node,
};
ScreenContextProvider.defaultProps = {
  children: null,
};

export function useScreenContext() {
  return (
    useContext(ScreenContext) || {
      screen: { route: "", screenName: "", data: null },
      setScreenContext: () => {},
    }
  );
}

// Human-readable name for a route (baseline awareness for screens that don't publish rich data).
export const SCREEN_NAMES = {
  "/home": "Home Dashboard",
  "/anticipate/HorizonScan": "Horizon Scan",
  "/anticipate/EmergingRisks": "Watchlist Risks",
  "/anticipate/board-paper": "Board Paper",
  "/anticipate/risk-advisor": "Risk Advisor",
  "/Taxonomy": "Taxonomy",
};

export function screenNameForRoute(pathname) {
  if (!pathname) return "";
  if (SCREEN_NAMES[pathname]) return SCREEN_NAMES[pathname];
  // Fall back to a title-cased last path segment.
  const seg = String(pathname).split("/").filter(Boolean).pop() || "";
  return seg.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
